import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Quill from 'quill';
import hljs from 'highlight.js';
import 'quill/dist/quill.snow.css';
import { Row, Col, Form, Button, Card, Modal, ListGroup, InputGroup } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Category, createCategory, deleteCategory, getAllCategories, updateCategory } from '../services/CategoryService';
import { AttachmentUploadResponse, ImageUploadResponse, InlineAttachmentUploadResponse, uploadAttachment, uploadImage, uploadImageFromUrl, uploadInlineAttachment } from '../services/ImageService';
import { createPost, getPost, updatePost } from '../services/PostService';
import { getValidAccessTokenOrRefresh } from '../utils/AuthTokenUtil';

const CODE_LANGUAGES = [
  { key: 'plain', label: 'Plain text' },
  { key: 'java', label: 'Java' },
  { key: 'javascript', label: 'JavaScript' },
  { key: 'typescript', label: 'TypeScript' },
  { key: 'perl', label: 'Perl' },
  { key: 'python', label: 'Python' },
  { key: 'sql', label: 'SQL' },
  { key: 'bash', label: 'Bash' },
];

const BlockEmbed = Quill.import('blots/block/embed') as any;

function formatFileSize(size: number) {
  if (!size) {
    return '';
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

class ImageCaptionBlot extends BlockEmbed {
  static blotName = 'imageCaption';
  static tagName = 'figure';
  static className = 'image-caption';

  static create(value: { src: string; caption?: string; thumbnailPriority?: boolean }) {
    const node = super.create() as HTMLElement;
    const img = document.createElement('img');
    const caption = document.createElement('figcaption');

    node.setAttribute('contenteditable', 'false');
    if (value.thumbnailPriority) {
      node.setAttribute('data-thumbnail-priority', 'true');
      img.setAttribute('data-thumbnail-priority', 'true');
    }
    img.setAttribute('src', value.src);
    img.setAttribute('alt', value.caption || '');
    caption.setAttribute('data-placeholder', '이미지 설명을 입력하세요.');
    caption.textContent = value.caption || '';

    node.appendChild(img);
    node.appendChild(caption);
    return node;
  }

  static value(node: HTMLElement) {
    const img = node.querySelector('img');
    const caption = node.querySelector('figcaption');
    const captionInput = node.querySelector<HTMLInputElement>('.image-caption-input');

    return {
      src: img?.getAttribute('src') || '',
      caption: captionInput?.value || caption?.textContent || '',
      thumbnailPriority: node.getAttribute('data-thumbnail-priority') === 'true' || img?.getAttribute('data-thumbnail-priority') === 'true',
    };
  }
}

Quill.register(ImageCaptionBlot, true);

class InlineAttachmentBlot extends BlockEmbed {
  static blotName = 'inlineAttachment';
  static tagName = 'div';
  static className = 'inline-attachment-card';

  static create(value: { downloadUrl: string; originalFilename: string; size?: number }) {
    const node = super.create() as HTMLElement;
    const link = document.createElement('a');
    const icon = document.createElement('span');
    const name = document.createElement('span');
    const meta = document.createElement('small');

    node.setAttribute('contenteditable', 'false');
    link.href = value.downloadUrl;
    link.className = 'inline-attachment-link';
    link.download = value.originalFilename;
    icon.className = 'inline-attachment-icon';
    icon.textContent = '📎';
    name.className = 'inline-attachment-name';
    name.textContent = value.originalFilename;
    meta.className = 'inline-attachment-size';
    meta.textContent = formatFileSize(value.size || 0);

    link.appendChild(icon);
    link.appendChild(name);
    link.appendChild(meta);
    node.appendChild(link);
    return node;
  }

  static value(node: HTMLElement) {
    const link = node.querySelector<HTMLAnchorElement>('a.inline-attachment-link');
    return {
      downloadUrl: link?.getAttribute('href') || '',
      originalFilename: link?.getAttribute('download') || link?.querySelector('.inline-attachment-name')?.textContent || '',
      size: 0,
    };
  }
}

Quill.register(InlineAttachmentBlot, true);

const getEditorHtml = (quill: Quill) => {
  const editor = quill.root.cloneNode(true) as HTMLElement;
  editor.querySelectorAll('figure.image-caption').forEach((figure) => {
    figure.removeAttribute('contenteditable');
  });
  editor.querySelectorAll('.inline-attachment-card').forEach((attachment) => {
    attachment.removeAttribute('contenteditable');
  });
  editor.querySelectorAll('figure.image-caption figcaption').forEach((caption) => {
    caption.removeAttribute('data-placeholder');
    const input = caption.querySelector<HTMLInputElement>('.image-caption-input');
    if (input) {
      caption.textContent = input.value.trim();
    }
  });
  return editor.innerHTML;
};

const openCaptionEditor = (figure: HTMLElement | null, onCommit: () => void) => {
  const caption = figure?.querySelector<HTMLElement>('figcaption');
  if (!caption) {
    return;
  }

  const existingInput = caption.querySelector<HTMLInputElement>('.image-caption-input');
  if (existingInput) {
    existingInput.focus({ preventScroll: true });
    existingInput.select();
    return;
  }

  const input = document.createElement('input');
  input.className = 'image-caption-input';
  input.type = 'text';
  input.value = caption.textContent || '';
  input.placeholder = '이미지 설명을 입력하세요.';

  const keepInputFocus = (event: Event) => {
    event.stopPropagation();
  };

  const openedAt = Date.now();
  let isCommitting = false;
  let ignoreNextEarlyBlur = true;

  const focusInput = (selectText = false) => {
    if (!caption.contains(input)) {
      return;
    }

    input.focus({ preventScroll: true });
    if (selectText) {
      input.select();
    }
  };

  const commit = () => {
    if (isCommitting || !caption.contains(input)) {
      return;
    }

    isCommitting = true;
    caption.textContent = input.value.trim();
    figure?.querySelector('img')?.setAttribute('alt', input.value.trim());
    onCommit();
  };

  input.addEventListener('pointerdown', keepInputFocus, true);
  input.addEventListener('pointerup', keepInputFocus, true);
  input.addEventListener('mousedown', keepInputFocus, true);
  input.addEventListener('mouseup', keepInputFocus, true);
  input.addEventListener('click', keepInputFocus, true);
  input.addEventListener('dblclick', keepInputFocus, true);
  input.addEventListener('paste', keepInputFocus, true);
  input.addEventListener('copy', keepInputFocus, true);
  input.addEventListener('cut', keepInputFocus, true);
  input.addEventListener('keydown', (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  });
  input.addEventListener('keyup', keepInputFocus, true);
  input.addEventListener('input', onCommit);
  input.addEventListener('blur', () => {
    window.setTimeout(() => {
      const elapsed = Date.now() - openedAt;
      if (ignoreNextEarlyBlur && elapsed < 900 && caption.contains(input)) {
        ignoreNextEarlyBlur = false;
        focusInput(false);
        return;
      }

      commit();
    }, 0);
  });

  caption.replaceChildren(input);
  window.setTimeout(() => focusInput(true), 0);
  window.setTimeout(() => focusInput(false), 80);
  window.setTimeout(() => focusInput(false), 220);
};

const dataUrlToFile = async (dataUrl: string) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const extension = blob.type.split('/')[1] || 'png';
  return new File([blob], `pasted-image.${extension}`, { type: blob.type });
};

const resizeImageFile = async (file: File, maxSize = 1400, quality = 0.82) => {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('이미지를 읽지 못했습니다.'));
      img.src = imageUrl;
    });

    const { naturalWidth, naturalHeight } = image;
    if (!naturalWidth || !naturalHeight) {
      return file;
    }

    const scale = Math.min(1, maxSize / Math.max(naturalWidth, naturalHeight));
    const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
    const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, outputType, outputType === 'image/jpeg' ? quality : undefined);
    });

    if (!blob || blob.size >= file.size) {
      return file;
    }

    const extension = outputType === 'image/png' ? 'png' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}-1400.${extension}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('Failed to resize image before upload', error);
    return file;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

const isDataImageSrc = (src: string) => src.startsWith('data:image/');

const getFirstImageSrcFromHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('img')?.getAttribute('src') || '';
};

const getImageSrcFromClipboard = (clipboardData: DataTransfer) => {
  const html = clipboardData.getData('text/html');
  const htmlImageSrc = html ? getFirstImageSrcFromHtml(html) : '';
  if (htmlImageSrc) {
    return htmlImageSrc;
  }

  const text = clipboardData.getData('text/plain').trim();
  if (text.startsWith('data:image/') || /^https?:\/\//i.test(text)) {
    return text;
  }

  return '';
};

const getVideoSrcFromInput = (input: string) => {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.includes('<iframe')) {
    const doc = new DOMParser().parseFromString(trimmed, 'text/html');
    return doc.querySelector('iframe')?.getAttribute('src') || '';
  }

  return trimmed;
};

const normalizeVideoUrl = (input: string) => {
  const videoSrc = getVideoSrcFromInput(input);
  if (!videoSrc) {
    return '';
  }

  try {
    const url = new URL(videoSrc, window.location.href);
    const hostname = url.hostname.replace(/^www\./, '');

    if (hostname === 'youtu.be') {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }

    if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
      const videoId = url.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }

      const paths = url.pathname.split('/').filter(Boolean);
      if ((paths[0] === 'embed' || paths[0] === 'shorts') && paths[1]) {
        return `https://www.youtube.com/embed/${paths[1]}`;
      }
    }

    if (hostname === 'tv.naver.com' || hostname === 'm.tv.naver.com') {
      const paths = url.pathname.split('/').filter(Boolean);
      const videoIndex = paths.indexOf('v');
      if (videoIndex >= 0 && paths[videoIndex + 1]) {
        return `https://tv.naver.com/embed/${paths[videoIndex + 1]}`;
      }

      const embedIndex = paths.indexOf('embed');
      if (embedIndex >= 0 && paths[embedIndex + 1]) {
        return `https://tv.naver.com/embed/${paths[embedIndex + 1]}`;
      }
    }

    if (hostname === 'serviceapi.nmv.naver.com') {
      return url.toString();
    }

    if (hostname === 'tv.kakao.com' || hostname === 'play-tv.kakao.com') {
      const paths = url.pathname.split('/').filter(Boolean);
      const cliplinkIndex = paths.indexOf('cliplink');
      if (cliplinkIndex >= 0 && paths[cliplinkIndex + 1]) {
        return `https://play-tv.kakao.com/embed/player/cliplink/${paths[cliplinkIndex + 1]}`;
      }

      const livelinkIndex = paths.indexOf('livelink');
      if (livelinkIndex >= 0 && paths[livelinkIndex + 1]) {
        return `https://play-tv.kakao.com/embed/player/livelink/${paths[livelinkIndex + 1]}`;
      }
    }

    return url.toString();
  } catch (error) {
    return '';
  }
};

const EditorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postId } = useParams();
  const restorePage = Number.isInteger(location.state?.restorePage) ? location.state.restorePage : 0;
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [loadedCategoryName, setLoadedCategoryName] = useState('');
  const [tagText, setTagText] = useState('');
  const [noticeStartDate, setNoticeStartDate] = useState('');
  const [noticeEndDate, setNoticeEndDate] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingCategoryParentId, setEditingCategoryParentId] = useState('');
  const [editingCategoryPrivate, setEditingCategoryPrivate] = useState(false);
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentUploadResponse[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isUploadingInlineAttachment, setIsUploadingInlineAttachment] = useState(false);
  const [imageDescription, setImageDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const inlineAttachmentInputRef = useRef<HTMLInputElement>(null);
  const pendingDataImageUploadsRef = useRef<Set<string>>(new Set());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const selectedCategory = categories.find((categoryItem) => String(categoryItem.id) === category);
  const categoryManagerCategories = useMemo(
    () => {
      const noticeCategories = categories.filter((categoryItem) => categoryItem.name === '공지사항');
      const normalCategories = categories.filter((categoryItem) => categoryItem.name !== '공지사항');
      const roots = normalCategories.filter((categoryItem) => !categoryItem.parentId);
      const childrenByParent = normalCategories.reduce((map, categoryItem) => {
        if (categoryItem.parentId) {
          const children = map.get(categoryItem.parentId) || [];
          children.push(categoryItem);
          map.set(categoryItem.parentId, children);
        }
        return map;
      }, new Map<number, Category[]>());

      return [
        ...roots.flatMap((root) => [root, ...(childrenByParent.get(root.id) || [])]),
        ...normalCategories.filter((categoryItem) => categoryItem.parentId && !normalCategories.some((parent) => parent.id === categoryItem.parentId)),
        ...noticeCategories,
      ];
    },
    [categories],
  );
  const rootCategoryOptions = categoryManagerCategories.filter((categoryItem) => !categoryItem.parentId && categoryItem.name !== '공지사항');
  const selectedRootCategoryId = selectedCategory?.parentId ? String(selectedCategory.parentId) : category;
  const childCategoryOptions = selectedRootCategoryId
    ? categoryManagerCategories.filter((categoryItem) => String(categoryItem.parentId || '') === selectedRootCategoryId)
    : [];
  const selectedChildCategoryId = selectedCategory?.parentId ? category : '';
  const isNoticeCategory = category.length > 0 && (selectedCategory?.name || loadedCategoryName) === '공지사항';
  const isPrivateCategory = Boolean(selectedCategory?.effectivePrivate ?? selectedCategory?.privateCategory);
  const isEditMode = Boolean(postId);
  const canSave = useMemo(
    () => !isSaving &&
      title.trim().length > 0 &&
      content.trim().length > 0 &&
      (!isNoticeCategory || (
        noticeStartDate.length > 0 &&
        noticeEndDate.length > 0 &&
        noticeStartDate <= noticeEndDate
      )),
    [content, isNoticeCategory, isSaving, noticeEndDate, noticeStartDate, title],
  );

  const insertUploadedImage = useCallback((quill: Quill, uploadedImage: ImageUploadResponse, captionText = '', thumbnailPriority = false) => {
    const currentSelection = quill.getSelection(true);
    const insertIndex = currentSelection?.index ?? quill.getLength();
    quill.insertEmbed(insertIndex, 'imageCaption', {
      src: uploadedImage.url,
      caption: captionText.trim(),
      thumbnailPriority,
    }, 'user');
    quill.insertText(insertIndex + 1, '\n', 'user');
    quill.setSelection(insertIndex + 2, 0, 'user');

    setContent(getEditorHtml(quill));
  }, []);

  const insertInlineAttachment = useCallback((quill: Quill, uploadedAttachment: InlineAttachmentUploadResponse) => {
    const currentSelection = quill.getSelection(true);
    const insertIndex = currentSelection?.index ?? quill.getLength();
    quill.insertEmbed(insertIndex, 'inlineAttachment', {
      downloadUrl: uploadedAttachment.downloadUrl,
      originalFilename: uploadedAttachment.originalFilename,
      size: uploadedAttachment.size,
    }, 'user');
    quill.insertText(insertIndex + 1, '\n', 'user');
    quill.setSelection(insertIndex + 2, 0, 'user');
    setContent(getEditorHtml(quill));
  }, []);

  const updateImageCaption = useCallback((target: EventTarget | null) => {
    const quill = quillRef.current;
    if (!quill || !(target instanceof HTMLElement)) {
      return false;
    }

    if (target.closest('.image-caption-input')) {
      return false;
    }

    const figure = target.closest<HTMLElement>('figure.image-caption');
    const clickedImage = target.closest<HTMLImageElement>('img');
    const captionRoot = figure || clickedImage;

    if (!captionRoot || !quill.root.contains(captionRoot)) {
      return false;
    }

    const img = figure?.querySelector<HTMLImageElement>('img') || clickedImage;
    const src = img?.getAttribute('src') || '';
    if (!src) {
      return false;
    }

    const targetNode = figure || img;
    if (!targetNode) {
      return false;
    }

    const blotNode = Quill.find(targetNode, true) as any;

    if (blotNode && typeof blotNode.offset === 'function') {
      const index = blotNode.offset(quill.scroll);
      if (!figure) {
        quill.deleteText(index, 1, 'user');
        quill.insertEmbed(index, 'imageCaption', { src, caption: img?.getAttribute('alt') || '' }, 'user');
        quill.insertText(index + 1, '\n', 'user');
      }

      window.setTimeout(() => {
        const figures = Array.from(quill.root.querySelectorAll<HTMLElement>('figure.image-caption'));
        const targetFigure = figures.find((item) => item.querySelector('img')?.getAttribute('src') === src) || null;
        openCaptionEditor(targetFigure, () => setContent(getEditorHtml(quill)));
      }, 0);
    } else if (figure) {
      const figcaption = figure.querySelector('figcaption') || document.createElement('figcaption');
      figcaption.setAttribute('data-placeholder', '이미지 설명을 입력하세요.');
      if (!figcaption.parentElement) {
        figure.appendChild(figcaption);
      }
      openCaptionEditor(figure, () => setContent(getEditorHtml(quill)));
    }

    setContent(getEditorHtml(quill));
    return true;
  }, []);

  const redirectToMainIfTokenExpired = useCallback(async () => {
    if (await getValidAccessTokenOrRefresh()) {
      return false;
    }

    alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
    navigate('/', { replace: true });
    return true;
  }, [navigate]);

  const openInlineAttachmentPicker = useCallback(() => {
    inlineAttachmentInputRef.current?.click();
  }, []);

  const handleInlineAttachmentFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const quill = quillRef.current;
    if (!file || !quill) {
      return;
    }

    setIsUploadingInlineAttachment(true);
    try {
      const token = await getValidAccessTokenOrRefresh();
      if (!token) {
        await redirectToMainIfTokenExpired();
        return;
      }

      const uploadedAttachment = await uploadInlineAttachment(file, token);
      insertInlineAttachment(quill, uploadedAttachment);
    } catch (error) {
      console.error('Failed to upload inline attachment', error);
      alert('본문 첨부파일 업로드에 실패했습니다. 허용 확장자와 파일 크기를 확인해주세요.');
    } finally {
      setIsUploadingInlineAttachment(false);
    }
  }, [insertInlineAttachment, redirectToMainIfTokenExpired]);

  const uploadDataImagesInEditor = useCallback(async (quill: Quill) => {
    const dataImages = Array.from(quill.root.querySelectorAll<HTMLImageElement>('img'))
      .filter((img) => isDataImageSrc(img.getAttribute('src') || ''));

    if (dataImages.length === 0) {
      return;
    }

    const token = await getValidAccessTokenOrRefresh();
    if (!token) {
      await redirectToMainIfTokenExpired();
      return;
    }

    for (const img of dataImages) {
      const src = img.getAttribute('src') || '';
      if (!src || pendingDataImageUploadsRef.current.has(src)) {
        continue;
      }

      pendingDataImageUploadsRef.current.add(src);
      try {
        const imageFile = await resizeImageFile(await dataUrlToFile(src));
        const uploadedImage = await uploadImage(imageFile, token);
        const figure = img.closest<HTMLElement>('figure.image-caption');

        if (figure) {
          img.setAttribute('src', uploadedImage.url);
          setContent(getEditorHtml(quill));
          continue;
        }

        const blotNode = Quill.find(img, true) as any;
        const index = blotNode && typeof blotNode.offset === 'function'
          ? blotNode.offset(quill.scroll)
          : -1;

        if (index >= 0) {
          quill.deleteText(index, 1, 'silent');
          quill.insertEmbed(index, 'imageCaption', { src: uploadedImage.url, caption: img.getAttribute('alt') || '' }, 'silent');
          quill.insertText(index + 1, '\n', 'silent');
          setContent(getEditorHtml(quill));
        }
      } catch (error) {
        console.error('Failed to upload pasted data image', error);
      } finally {
        pendingDataImageUploadsRef.current.delete(src);
      }
    }
  }, [redirectToMainIfTokenExpired]);

  const loadCategories = useCallback(async () => {
    const token = await getValidAccessTokenOrRefresh();
    const data = await getAllCategories(token);
    setCategories(data);
    setCategory((currentCategory) => (
      currentCategory && data.some((categoryItem) => String(categoryItem.id) === currentCategory)
        ? currentCategory
        : ''
    ));
  }, []);

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const resizedFile = await resizeImageFile(file);
      setImageFile(resizedFile);
      setImagePreviewUrl(URL.createObjectURL(resizedFile));
    }
  };

  const getTags = () => {
    return Array.from(new Set(
      tagText
        .split(/[,\s]+/)
        .map((tag) => tag.trim())
        .filter(Boolean),
    ));
  };

  const handleApplyImage = async () => {
    const quill = quillRef.current;
    const token = await getValidAccessTokenOrRefresh();

    if (!quill || !imageFile) {
      return;
    }

    if (!token) {
      await redirectToMainIfTokenExpired();
      return;
    }

    setIsUploadingImage(true);
    try {
      const uploadedImage = await uploadImage(imageFile, token);
      insertUploadedImage(quill, uploadedImage, imageDescription, true);
      setImageFile(null);
      setImagePreviewUrl('');
      setImageDescription('');
    } catch (error) {
      console.error('Failed to upload image', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) {
      return;
    }

    const token = await getValidAccessTokenOrRefresh();
    if (!token) {
      await redirectToMainIfTokenExpired();
      return;
    }

    setIsUploadingAttachment(true);
    try {
      const uploadedAttachments = await Promise.all(files.map((file) => uploadAttachment(file, token)));
      setAttachments((current) => [...current, ...uploadedAttachments]);
    } catch (error) {
      console.error('Failed to upload attachment', error);
      alert('첨부파일 업로드에 실패했습니다.');
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (storagePath: string) => {
    setAttachments((current) => current.filter((attachment) => attachment.storagePath !== storagePath));
  };

  const getCategoryActionToken = async () => {
    const token = await getValidAccessTokenOrRefresh();
    if (!token) {
      await redirectToMainIfTokenExpired();
      return '';
    }
    return token;
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      alert('카테고리명을 입력해주세요.');
      return;
    }

    const token = await getCategoryActionToken();
    if (!token) {
      return;
    }

    setCategoryActionLoading(true);
    try {
      await createCategory(name, false, token, newCategoryParentId ? Number(newCategoryParentId) : null);
      setNewCategoryName('');
      setNewCategoryParentId('');
      await loadCategories();
    } catch (error) {
      console.error('Failed to create category', error);
      alert('카테고리 추가에 실패했습니다.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleStartEditCategory = (categoryItem: Category) => {
    setEditingCategoryId(categoryItem.id);
    setEditingCategoryName(categoryItem.name);
    setEditingCategoryParentId(categoryItem.parentId ? String(categoryItem.parentId) : '');
    setEditingCategoryPrivate(categoryItem.privateCategory);
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
    setEditingCategoryParentId('');
    setEditingCategoryPrivate(false);
  };

  const handleUpdateCategory = async () => {
    const name = editingCategoryName.trim();
    if (!editingCategoryId || !name) {
      alert('카테고리명을 입력해주세요.');
      return;
    }

    const token = await getCategoryActionToken();
    if (!token) {
      return;
    }

    setCategoryActionLoading(true);
    try {
      await updateCategory(editingCategoryId, name, editingCategoryPrivate, token, editingCategoryParentId ? Number(editingCategoryParentId) : null);
      handleCancelEditCategory();
      await loadCategories();
    } catch (error) {
      console.error('Failed to update category', error);
      alert('카테고리 수정에 실패했습니다.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleToggleCategoryPrivate = async (categoryItem: Category, privateCategory: boolean) => {
    const token = await getCategoryActionToken();
    if (!token) {
      return;
    }

    setCategoryActionLoading(true);
    try {
      await updateCategory(categoryItem.id, categoryItem.name, privateCategory, token, categoryItem.parentId || null);
      await loadCategories();
    } catch (error) {
      console.error('Failed to update category privacy', error);
      alert('카테고리 비공개 설정 변경에 실패했습니다.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryItem: Category) => {
    if (categoryItem.name === '공지사항') {
      alert('공지사항 카테고리는 삭제할 수 없습니다.');
      return;
    }

    if (!window.confirm(`'${categoryItem.name}' 카테고리를 삭제하시겠습니까?\n관련 게시물은 메인 화면에서 조회되지 않습니다.`)) {
      return;
    }

    const token = await getCategoryActionToken();
    if (!token) {
      return;
    }

    setCategoryActionLoading(true);
    try {
      await deleteCategory(categoryItem.id, token);
      if (category === String(categoryItem.id)) {
        setCategory('');
      }
      if (editingCategoryId === categoryItem.id) {
        handleCancelEditCategory();
      }
      await loadCategories();
    } catch (error) {
      console.error('Failed to delete category', error);
      alert('카테고리 삭제에 실패했습니다.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  useEffect(() => {
    redirectToMainIfTokenExpired();
  }, [redirectToMainIfTokenExpired]);

  useEffect(() => {
    if (!toolbarRef.current || !editorRef.current || quillRef.current) {
      return;
    }

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: toolbarRef.current,
        },
        syntax: {
          hljs,
          interval: 500,
          languages: CODE_LANGUAGES,
        },
      },
    });

    const toolbar = quill.getModule('toolbar') as any;
    toolbar.addHandler('video', () => {
      const input = window.prompt('YouTube 또는 네이버 영상 URL을 입력하세요.');
      if (input === null) {
        return;
      }

      const videoUrl = normalizeVideoUrl(input);
      if (!videoUrl) {
        alert('지원하지 않는 영상 URL입니다.');
        return;
      }

      const currentSelection = quill.getSelection(true);
      const insertIndex = currentSelection?.index ?? quill.getLength();
      quill.insertEmbed(insertIndex, 'video', videoUrl, 'user');
      quill.insertText(insertIndex + 1, '\n', 'user');
      quill.setSelection(insertIndex + 2, 0, 'user');
      setContent(getEditorHtml(quill));
    });

    quill.on('text-change', () => {
      setContent(getEditorHtml(quill));
      window.setTimeout(() => {
        uploadDataImagesInEditor(quill);
      }, 0);
    });

    const dataImageObserver = new MutationObserver(() => {
      uploadDataImagesInEditor(quill);
    });
    dataImageObserver.observe(quill.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    });

    const handleImageCaptionPointerEvent = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest('.image-caption-input')) {
        event.stopPropagation();
        return;
      }

      if (!updateImageCaption(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };

    const handlePaste = async (event: ClipboardEvent) => {
      const pasteTarget = event.target as HTMLElement;
      if (!quill.root.contains(pasteTarget) || pasteTarget.closest('.image-caption-input')) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      const imageFileFromClipboard =
        Array.from(clipboardData.files).find((file) => file.type.startsWith('image/')) ||
        Array.from(clipboardData.items)
          .find((item) => item.kind === 'file' && item.type.startsWith('image/'))
          ?.getAsFile() ||
        null;
      const imageSrc = getImageSrcFromClipboard(clipboardData);

      if (!imageFileFromClipboard && !imageSrc) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setIsUploadingImage(true);

      try {
        const token = await getValidAccessTokenOrRefresh();
        if (!token) {
          await redirectToMainIfTokenExpired();
          return;
        }

        let uploadedImage: ImageUploadResponse;
        if (imageFileFromClipboard) {
          uploadedImage = await uploadImage(await resizeImageFile(imageFileFromClipboard), token);
        } else if (imageSrc.startsWith('data:image/')) {
          const imageFile = await resizeImageFile(await dataUrlToFile(imageSrc));
          uploadedImage = await uploadImage(imageFile, token);
        } else {
          uploadedImage = await uploadImageFromUrl(new URL(imageSrc, window.location.href).toString(), token);
        }

        insertUploadedImage(quill, uploadedImage);
      } catch (error) {
        console.error('Failed to paste image', error);
        alert('붙여넣은 이미지 업로드에 실패했습니다.');
      } finally {
        setIsUploadingImage(false);
      }
    };

    document.addEventListener('paste', handlePaste, true);
    quill.root.addEventListener('mousedown', handleImageCaptionPointerEvent, true);
    quill.root.addEventListener('mouseup', handleImageCaptionPointerEvent, true);
    quill.root.addEventListener('click', handleImageCaptionPointerEvent, true);
    quillRef.current = quill;

    return () => {
      dataImageObserver.disconnect();
      document.removeEventListener('paste', handlePaste, true);
      quill.root.removeEventListener('mousedown', handleImageCaptionPointerEvent, true);
      quill.root.removeEventListener('mouseup', handleImageCaptionPointerEvent, true);
      quill.root.removeEventListener('click', handleImageCaptionPointerEvent, true);
    };
  }, [insertUploadedImage, redirectToMainIfTokenExpired, updateImageCaption, uploadDataImagesInEditor]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    loadCategories()
      .catch((error) => {
        console.error('Failed to load categories', error);
      });
  }, [loadCategories]);

  useEffect(() => {
    if (!postId) {
      return;
    }

    getValidAccessTokenOrRefresh()
      .then((token) => getPost(postId, token))
      .then((post) => {
        setTitle(post.title || '');
        setContent(post.content || '');
        setCategory(post.category?.id ? String(post.category.id) : '');
        setLoadedCategoryName(post.category?.name || '');
        setTagText((post.tags || []).map((tag: { name: string }) => tag.name).join(' '));
        setAttachments(post.attachments || []);
        setNoticeStartDate(post.noticeStartDate || '');
        setNoticeEndDate(post.noticeEndDate || '');

        if (quillRef.current) {
          quillRef.current.clipboard.dangerouslyPasteHTML(post.content || '');
          setContent(getEditorHtml(quillRef.current));
        }
      })
      .catch((error) => {
        console.error('Failed to load post', error);
        alert('게시글을 불러오지 못했습니다.');
        navigate('/', { replace: true });
      });
  }, [navigate, postId]);

  useEffect(() => {
    if (!isNoticeCategory) {
      setNoticeStartDate('');
      setNoticeEndDate('');
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    setNoticeStartDate((current) => current || today);
    setNoticeEndDate((current) => current || today);
  }, [isNoticeCategory]);

  const handleSave = async () => {
    const latestContent = quillRef.current ? getEditorHtml(quillRef.current) : content;
    const token = await getValidAccessTokenOrRefresh();

    if (!token) {
      await redirectToMainIfTokenExpired();
      return;
    }

    setIsSaving(true);
    try {
      const postData = {
        title: title.trim(),
        content: latestContent,
        categoryId: category ? Number(category) : null,
        tags: isNoticeCategory ? [] : getTags(),
        attachments: isPrivateCategory ? attachments : [],
        noticeStartDate: isNoticeCategory ? noticeStartDate : null,
        noticeEndDate: isNoticeCategory ? noticeEndDate : null,
      };

      if (isEditMode && postId) {
        await updatePost(postId, postData, token);
      } else {
        await createPost(postData, token);
      }

      alert(isEditMode ? '게시글이 수정되었습니다.' : '게시글이 저장되었습니다.');
      navigate('/', { state: { refreshPosts: true, restorePage: isEditMode ? restorePage : 0 }, replace: true });
    } catch (error) {
      console.error('Failed to save post', error);
      alert('게시글 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCaptionPointerCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('.image-caption-input')) {
      event.stopPropagation();
      return;
    }

    if (updateImageCaption(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleRootCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(event.target.value);
  };

  const handleChildCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(event.target.value || selectedRootCategoryId || '');
  };

  return (
    <section className="writer-page">
      <Row className="writer-grid">
        <Col lg={8} className="writer-main">
          <div className="writer-surface">
            <Form.Group className="mb-3">
              <div className="writer-category-label-row">
                <Form.Label className="mb-0">카테고리</Form.Label>
              </div>
              <div className="writer-category-control-row">
                <Form.Select value={selectedRootCategoryId} onChange={handleRootCategoryChange} aria-label="상위 카테고리 선택">
                  <option value="">상위 카테고리 선택</option>
                  {categoryManagerCategories.filter((categoryItem) => !categoryItem.parentId).map((categoryItem) => (
                    <option key={categoryItem.id} value={categoryItem.id}>
                      {categoryItem.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select value={selectedChildCategoryId} onChange={handleChildCategoryChange} aria-label="하위 카테고리 선택" disabled={!selectedRootCategoryId || childCategoryOptions.length === 0}>
                  <option value="">하위 카테고리 선택</option>
                  {childCategoryOptions.map((categoryItem) => (
                    <option key={categoryItem.id} value={categoryItem.id}>
                      {categoryItem.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="writer-category-manage-button"
                  onClick={() => setShowCategoryManager(true)}
                >
                  관리
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>제목</Form.Label>
              <Form.Control
                type="text"
                placeholder="제목을 입력하세요"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="writer-title-input"
              />
            </Form.Group>

            {isNoticeCategory ? (
              <div className="notice-period-row mb-4">
                <Form.Group>
                  <Form.Label>공지 시작일자</Form.Label>
                  <Form.Control
                    type="date"
                    value={noticeStartDate}
                    onChange={(e) => setNoticeStartDate(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>공지 종료일자</Form.Label>
                  <Form.Control
                    type="date"
                    value={noticeEndDate}
                    min={noticeStartDate || undefined}
                    onChange={(e) => setNoticeEndDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </div>
            ) : (
              <Form.Group className="mb-4">
                <Form.Label>태그</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="쉼표 또는 공백으로 태그를 구분하세요"
                  value={tagText}
                  onChange={(e) => setTagText(e.target.value)}
                />
              </Form.Group>
            )}

            <div
              className="editor-wrapper"
              onPasteCapture={(event) => {
                if ((event.target as HTMLElement).closest('.image-caption-input')) {
                  event.stopPropagation();
                }
              }}
              onMouseDownCapture={handleCaptionPointerCapture}
              onMouseUpCapture={handleCaptionPointerCapture}
              onClickCapture={handleCaptionPointerCapture}
            >
              <input
                ref={inlineAttachmentInputRef}
                type="file"
                accept=".pdf,.txt,.zip,.docx,.xlsx,.pptx,.hwp,.hwpx"
                className="d-none"
                onChange={handleInlineAttachmentFileChange}
              />
              <div ref={toolbarRef} className="editor-toolbar">
                <span className="ql-formats">
                  <select className="ql-header" defaultValue="">
                    <option value="1">제목 1</option>
                    <option value="2">제목 2</option>
                    <option value="">본문</option>
                  </select>
                </span>
                <span className="ql-formats">
                  <button className="ql-bold" type="button" aria-label="굵게" />
                  <button className="ql-italic" type="button" aria-label="기울임" />
                  <button className="ql-underline" type="button" aria-label="밑줄" />
                  <button className="ql-strike" type="button" aria-label="취소선" />
                  <button className="ql-blockquote" type="button" aria-label="인용" />
                </span>
                <span className="ql-formats">
                  <button className="ql-list" value="ordered" type="button" aria-label="번호 목록" />
                  <button className="ql-list" value="bullet" type="button" aria-label="글머리 목록" />
                  <button className="ql-indent" value="-1" type="button" aria-label="내어쓰기" />
                  <button className="ql-indent" value="+1" type="button" aria-label="들여쓰기" />
                </span>
                <span className="ql-formats">
                  <button className="ql-link" type="button" aria-label="링크" />
                  <button
                    className="editor-attachment-button"
                    type="button"
                    aria-label="첨부파일"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={openInlineAttachmentPicker}
                  >
                    📎
                  </button>
                  <button className="ql-video" type="button" aria-label="영상" />
                  <button className="ql-code-block" type="button" aria-label="코드 블록" />
                </span>
                <span className="ql-formats">
                  <button className="ql-clean" type="button" aria-label="서식 제거" />
                </span>
              </div>
              <div ref={editorRef} className="quill-editor" />
            </div>
            {isUploadingInlineAttachment && <div className="attachment-uploading">본문 첨부파일 업로드 중...</div>}

            <div className="writer-actions">
              <Button variant="dark" onClick={handleSave} disabled={!canSave}>
                {isSaving ? '저장 중...' : '발행하기'}
              </Button>
            </div>
          </div>
        </Col>

        <Col lg={4} className="writer-aside">
          <Card className="writer-card">
            <Card.Header>이미지 수동 등록</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>이미지 선택</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={onSelectFile} />
              </Form.Group>

              <div className="image-preview-panel">
                {imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt="선택한 이미지 미리보기" />
                ) : (
                  <div className="image-preview-empty">선택한 이미지가 여기에 표시됩니다.</div>
                )}
              </div>

              <Form.Group className="mt-3">
                <Form.Label>이미지 설명</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={imageDescription}
                  onChange={(event) => setImageDescription(event.target.value)}
                  placeholder="이미지 하단에 표시할 설명을 입력하세요."
                />
              </Form.Group>

              <Button
                variant="outline-dark"
                size="sm"
                className="w-100 mt-3"
                disabled={!imageFile || isUploadingImage}
                onClick={handleApplyImage}
              >
                {isUploadingImage ? '업로드 중...' : '이미지 적용'}
              </Button>
            </Card.Body>
          </Card>

          {isPrivateCategory && (
            <Card className="writer-card">
              <Card.Header>첨부파일</Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>파일 선택</Form.Label>
                  <Form.Control type="file" multiple onChange={handleAttachmentUpload} disabled={isUploadingAttachment} />
                </Form.Group>
                {isUploadingAttachment && <div className="attachment-uploading">업로드 중...</div>}
                {attachments.length > 0 ? (
                  <div className="attachment-edit-list">
                    {attachments.map((attachment) => (
                      <div key={attachment.storagePath} className="attachment-edit-item">
                        <span>{attachment.originalFilename}</span>
                        <button type="button" aria-label="첨부파일 제거" onClick={() => handleRemoveAttachment(attachment.storagePath)}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="attachment-empty">등록된 첨부파일이 없습니다.</div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Modal show={showCategoryManager} onHide={() => setShowCategoryManager(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>카테고리 관리</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="category-manager-add">
            <Form.Control
              value={newCategoryName}
              onChange={(event) => setNewCategoryName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleCreateCategory();
                }
              }}
              placeholder="새 카테고리명"
              disabled={categoryActionLoading}
            />
            <Button
              type="button"
              variant="dark"
              onClick={handleCreateCategory}
              disabled={categoryActionLoading || newCategoryName.trim().length === 0}
            >
              추가
            </Button>
          </InputGroup>
          <Form.Select
            className="category-manager-parent-select"
            value={newCategoryParentId}
            onChange={(event) => setNewCategoryParentId(event.target.value)}
            disabled={categoryActionLoading}
          >
            <option value="">상위 카테고리 없음</option>
            {rootCategoryOptions.map((categoryItem) => (
              <option key={categoryItem.id} value={categoryItem.id}>{categoryItem.name}</option>
            ))}
          </Form.Select>

          <ListGroup className="category-manager-list">
            {categoryManagerCategories.map((categoryItem) => {
              const isEditing = editingCategoryId === categoryItem.id;
              const isFixedCategory = categoryItem.name === '공지사항';
              return (
                <ListGroup.Item key={categoryItem.id} className="category-manager-item">
                  {isEditing ? (
                    <div className="category-manager-edit-form">
                      <InputGroup>
                      <Form.Control
                        value={editingCategoryName}
                        onChange={(event) => setEditingCategoryName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleUpdateCategory();
                          }
                        }}
                        autoFocus
                        disabled={categoryActionLoading}
                      />
                      <InputGroup.Checkbox
                        aria-label="Private 카테고리"
                        checked={editingCategoryPrivate}
                        onChange={(event) => setEditingCategoryPrivate(event.currentTarget.checked)}
                        disabled={categoryActionLoading}
                      />
                      <Button
                        type="button"
                        variant="dark"
                        onClick={handleUpdateCategory}
                        disabled={categoryActionLoading || editingCategoryName.trim().length === 0}
                      >
                        저장
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={handleCancelEditCategory}
                        disabled={categoryActionLoading}
                      >
                        취소
                      </Button>
                      </InputGroup>
                      <Form.Select
                        value={editingCategoryParentId}
                        onChange={(event) => setEditingCategoryParentId(event.target.value)}
                        disabled={categoryActionLoading}
                      >
                        <option value="">상위 카테고리 없음</option>
                        {rootCategoryOptions
                          .filter((parentCategory) => parentCategory.id !== categoryItem.id)
                          .map((parentCategory) => (
                            <option key={parentCategory.id} value={parentCategory.id}>{parentCategory.name}</option>
                          ))}
                      </Form.Select>
                    </div>
                  ) : (
                    <>
                      <div className="category-manager-name">
                        <span className={categoryItem.parentId ? 'category-manager-child-name' : ''}>{categoryItem.parentId ? `└ ${categoryItem.name}` : categoryItem.name}</span>
                        <small>{isFixedCategory ? '고정 카테고리' : `${categoryItem.parentName ? `${categoryItem.parentName} 하위 · ` : ''}${categoryItem.postCount}건${categoryItem.effectivePrivate && !categoryItem.privateCategory ? ' · 상위 비공개 적용' : ''}`}</small>
                      </div>
                      {isFixedCategory ? (
                        <span className="category-manager-fixed">고정</span>
                      ) : (
                        <div className="category-manager-actions">
                          <Form.Check
                            className="category-manager-private-toggle"
                            type="checkbox"
                            label="비공개"
                            checked={categoryItem.privateCategory}
                            onChange={(event) => handleToggleCategoryPrivate(categoryItem, event.target.checked)}
                            disabled={categoryActionLoading}
                          />
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            aria-label={`${categoryItem.name} 수정`}
                            onClick={() => handleStartEditCategory(categoryItem)}
                            disabled={categoryActionLoading}
                          >
                            <i className="bi bi-pencil" aria-hidden="true" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            aria-label={`${categoryItem.name} 삭제`}
                            onClick={() => handleDeleteCategory(categoryItem)}
                            disabled={categoryActionLoading}
                          >
                            <i className="bi bi-trash" aria-hidden="true" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCategoryManager(false)} disabled={categoryActionLoading}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default EditorPage;
